import React from 'react';
import {
  View, StyleSheet, Text, Pressable,
} from 'react-native';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import FlagIcon16 from '../../16pxIcons/FlagIcon16';
import EditIcon24 from '../../24pxIcons/EditIcon24';
import DeleteIcon24 from '../../24pxIcons/DeleteIcon24';
import ImageIcon24 from '../../ImageIcon24';
import { LocationType, MeterLabel, SensorLabel } from '../../../constants/Types';

export const priorityTitle = (priority: boolean) => (priority ? 'High' : 'Normal');

export const priorityColor = (priority: boolean) => (priority
  ? Colors.light.hereworksOrange500 : Colors.light.hereworksGreen500);

// eslint-disable-next-line no-shadow
export enum AreaType {
  // eslint-disable-next-line no-unused-vars
  IT_HELPDESK = 'IT_HELPDESK', RECEPTION = 'RECEPTION', MEETING_ROOM = 'MEETING_ROOM',
  // eslint-disable-next-line no-unused-vars
  CAFE = 'CAFE', TOILET = 'TOILET', GYM = 'GYM', QUEUE = 'QUEUE', OFFICE = 'OFFICE',
  // eslint-disable-next-line no-unused-vars
  OPEN_PLAN_WORKSPACE = 'OPEN_PLAN_WORKSPACE', CONFERENCE_ROOM = 'CONFERENCE_ROOM',
  // eslint-disable-next-line no-unused-vars
  SHOWER = 'SHOWER', CHANGING_ROOM = 'CHANGING_ROOM', BOOTH = 'BOOTH',
  // eslint-disable-next-line no-unused-vars
  BREAK_OUT_SPACE = 'BREAK_OUT_SPACE',
}

export const LocationSubtypeTitles = {
  [AreaType.IT_HELPDESK]: 'IT Helpdesk',
  [AreaType.RECEPTION]: 'Reception',
  [AreaType.MEETING_ROOM]: 'Meeting room',
  [AreaType.CAFE]: 'Cafe',
  [AreaType.TOILET]: 'Toilet',
  [AreaType.GYM]: 'Gym',
  [AreaType.QUEUE]: 'Queue',
  [AreaType.OFFICE]: 'Office',
  [AreaType.OPEN_PLAN_WORKSPACE]: 'Open Plan workspace',
  [AreaType.CONFERENCE_ROOM]: 'Conference room',
  [AreaType.SHOWER]: 'Shower',
  [AreaType.CHANGING_ROOM]: 'Changing room',
  [AreaType.BOOTH]: 'Booth',
  [AreaType.BREAK_OUT_SPACE]: 'Break out space',
};

export type Region = {
  Name: string,
  LocationId: string,
  ParentId: string,
  IsHighPriority: boolean,
  City?: string,
  Country?: string,
  assetCount?: number,
  sensorCount?: number,
  meterCount?: number,
  assetCountByLabel?: Record<SensorLabel | MeterLabel, number>,
  floorName?: string,
  areaName?: string,
  buildingId?: string,
  ExternalId?: string,
  Type: LocationType,
}

type BoundRegion = {
  Points?: { X: number, Number: number, Y: number }[],
  Polygon?: string,
  Top?: number,
  Left?: number,
  Right?: number,
  Bottom?: number,
  LocationCapacity?: number,
  floorId?: string,
}

export interface Space extends Region, BoundRegion {
  LocationSubtype?: AreaType,
  AreaName?: string,
  FloorName?: string,
}

export interface Area extends Region, BoundRegion {
  spaces?: object,
  spaceCount?: number,
  LocationSubtype?: AreaType,
  FloorName?: string,
}

export interface Floor extends Region {
  Number: string,
  plan?: string,
  imageSize: number,
  planFilename: string,
  Svg?: string,
  Index?: number,
  areas?: object,
  motionDetected?: boolean,
  guardLastChecked?: string;
}

interface FloorItemProps extends Floor {
  index: number,
  edit: () => void,
  remove: () => void,
  drag?: () => void,
  isActive?: boolean,
  listeners?: any,
  attributes?: any,
}

const FloorItem = ({
  Name, Number, IsHighPriority = false, index, edit, remove, plan,
  imageSize, planFilename, isActive, listeners, attributes,
}: FloorItemProps) => <View style={styles.container}>
    <Text style={styles.index}>{index + 1}</Text>
    <View style={styles.content}>
      <View style={[Style.row, styles.stretch]}>
        <View style={Style.row}>
          <Text style={styles.index}>{`FLR: ${Number ?? '-'}`}</Text>
          <View style={[
            Style.row, styles.priorityIndicator,
            { borderColor: priorityColor(IsHighPriority) },
          ]}>
            <FlagIcon16 color={priorityColor(IsHighPriority)} />
            <Text style={[styles.priorityText, { color: priorityColor(IsHighPriority) }]}>
              {priorityTitle(IsHighPriority)}
            </Text>
          </View>
        </View>
        <View style={Style.row}>
          <Pressable
            style={(state) => [styles.editIcon, state.hovered && Style.hovered]}
            onPress={edit}
            testID='map-edit-floors-details-btn'>
            <EditIcon24 />
          </Pressable>
          <Pressable
            style={(state) => [state.hovered && Style.hovered]}
            onPress={remove}
            testID='map-delete-floor-btn'>
            <DeleteIcon24 />
          </Pressable>
        </View>
      </View>
      <Text style={styles.title}>{Name ?? '-'}</Text>
      <View style={styles.separator} />
      <View style={[Style.row, styles.subSection]}>
        <View style={[Style.row, { flex: 1 }]}>
          <ImageIcon24
            color={(plan || planFilename) ? Colors.light.hereworksBlue400 : styles.error.color}
            style={styles.editIcon}
          />
          <Text
            style={[styles.filename, !(plan || planFilename) && styles.error]}
            numberOfLines={1}
            ellipsizeMode='middle'
          >
            {(plan || planFilename) ? `${planFilename?.split('/').pop() ?? 'Unknown file'}`
              : 'Please upload a floor plan'}
          </Text>
        </View>
        {(plan || planFilename) && <Text style={styles.index}>{`${imageSize ?? '-'} MB`}</Text>}
      </View>
      <div
        style={{ cursor: isActive ? 'grabbing' : 'grab' }}
        {...attributes}
        {...listeners}
      >
        <View style={[Style.row, styles.dotsRow]}>
          {Array.from({ length: 30 }, (_, dotIndex: number) => <View
            style={styles.dot}
            key={`dot_first_row_${dotIndex}`}
          />)}
        </View>
        <View style={[Style.row, styles.dotsRow]}>
          {Array.from({ length: 30 }, (_, dotIndex: number) => <View
            style={styles.dot}
            key={`dot_second_row_${dotIndex}`}
          />)}
        </View>
      </div>
    </View>
  </View>;

const styles = StyleSheet.create({
  stretch: {
    alignSelf: 'stretch',
  },
  container: {
    marginEnd: 24,
    flexDirection: 'row',
    alignSelf: 'stretch',
    marginBottom: 8,
  },
  index: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
    textAlign: 'center',
    marginEnd: 10,
  },
  content: {
    padding: 16,
    paddingTop: 12,
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite100,
    flex: 1,
    borderWidth: 1,
  },
  title: {
    fontSize: 18,
    lineHeight: 22,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    marginTop: 8,
    marginBottom: 12,
  },
  separator: {
    alignSelf: 'stretch',
    height: 1,
    backgroundColor: Colors.light.hereworksBlue100,
    marginBottom: 12,
  },
  editIcon: {
    marginEnd: 8,
  },
  priorityIndicator: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 4,
    alignSelf: 'flex-end',
    backgroundColor: '#FFF',
  },
  priorityText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    marginStart: 2,
  },
  dotsRow: {
    overflow: 'hidden',
    flexShrink: 1,
    zIndex: -1,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 3,
    backgroundColor: Colors.light.hereworksDarkBlue100,
    marginHorizontal: 5,
    marginTop: 10,
  },
  filename: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 12,
    flex: 1,
  },
  error: {
    color: Colors.light.hereworksRed500,
  },
  subSection: {
    width: '100%',
  },
});

export default FloorItem;
